import React, { useCallback, useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { BuildingIcon, MailIcon, PhoneIcon, AlertCircleIcon, CheckCircleIcon, CalendarIcon, CreditCardIcon, ArrowRightIcon, ArrowLeftIcon, ShieldCheckIcon, Loader2Icon, UserIcon, MapPinIcon, ClipboardListIcon, PartyPopperIcon, ExternalLinkIcon, FileTextIcon } from 'lucide-react';
const RECAPTCHA_SITE_KEY = '6LdlbBgUAAAAAJ9LBivF_koW3ytmjuIp-vBNuiFn';
// Declare grecaptcha global
declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}
// US States
const US_STATES = [{
  value: '',
  label: 'Select state...'
}, {
  value: 'AL',
  label: 'Alabama'
}, {
  value: 'AK',
  label: 'Alaska'
}, {
  value: 'AZ',
  label: 'Arizona'
}, {
  value: 'AR',
  label: 'Arkansas'
}, {
  value: 'CA',
  label: 'California'
}, {
  value: 'CO',
  label: 'Colorado'
}, {
  value: 'CT',
  label: 'Connecticut'
}, {
  value: 'DE',
  label: 'Delaware'
}, {
  value: 'DC',
  label: 'District of Columbia'
}, {
  value: 'FL',
  label: 'Florida'
}, {
  value: 'GA',
  label: 'Georgia'
}, {
  value: 'HI',
  label: 'Hawaii'
}, {
  value: 'ID',
  label: 'Idaho'
}, {
  value: 'IL',
  label: 'Illinois'
}, {
  value: 'IN',
  label: 'Indiana'
}, {
  value: 'IA',
  label: 'Iowa'
}, {
  value: 'KS',
  label: 'Kansas'
}, {
  value: 'KY',
  label: 'Kentucky'
}, {
  value: 'LA',
  label: 'Louisiana'
}, {
  value: 'ME',
  label: 'Maine'
}, {
  value: 'MD',
  label: 'Maryland'
}, {
  value: 'MA',
  label: 'Massachusetts'
}, {
  value: 'MI',
  label: 'Michigan'
}, {
  value: 'MN',
  label: 'Minnesota'
}, {
  value: 'MS',
  label: 'Mississippi'
}, {
  value: 'MO',
  label: 'Missouri'
}, {
  value: 'MT',
  label: 'Montana'
}, {
  value: 'NE',
  label: 'Nebraska'
}, {
  value: 'NV',
  label: 'Nevada'
}, {
  value: 'NH',
  label: 'New Hampshire'
}, {
  value: 'NJ',
  label: 'New Jersey'
}, {
  value: 'NM',
  label: 'New Mexico'
}, {
  value: 'NY',
  label: 'New York'
}, {
  value: 'NC',
  label: 'North Carolina'
}, {
  value: 'ND',
  label: 'North Dakota'
}, {
  value: 'OH',
  label: 'Ohio'
}, {
  value: 'OK',
  label: 'Oklahoma'
}, {
  value: 'OR',
  label: 'Oregon'
}, {
  value: 'PA',
  label: 'Pennsylvania'
}, {
  value: 'RI',
  label: 'Rhode Island'
}, {
  value: 'SC',
  label: 'South Carolina'
}, {
  value: 'SD',
  label: 'South Dakota'
}, {
  value: 'TN',
  label: 'Tennessee'
}, {
  value: 'TX',
  label: 'Texas'
}, {
  value: 'UT',
  label: 'Utah'
}, {
  value: 'VT',
  label: 'Vermont'
}, {
  value: 'VA',
  label: 'Virginia'
}, {
  value: 'WA',
  label: 'Washington'
}, {
  value: 'WV',
  label: 'West Virginia'
}, {
  value: 'WI',
  label: 'Wisconsin'
}, {
  value: 'WY',
  label: 'Wyoming'
}];
// API Interfaces
interface ApiSubscriptionItem {
  title: string;
  qty: number;
  price: number;
  amount: number;
  taxable?: number;
}
interface ApiCompany {
  name: string;
  email?: string;
  phone?: string;
  logo?: string;
}
interface PublicSubscriptionTemplateData {
  id: string;
  company_id: string;
  name: string;
  cycle: string;
  cycle_num: number;
  installments: number;
  items: ApiSubscriptionItem[];
  amount: number;
  tax: number;
  tax_label?: string;
  fee?: number;
  fee_label?: string;
  company: ApiCompany;
}
interface SubscriptionResult {
  amount: number;
  invoice_number: string;
  invoice_link: string;
}
// Helper to format billing cycle
const formatBillingCycle = (cycle: string, cycleNum: number): string => {
  const num = Number(cycleNum);
  const cycleNames: Record<string, {
    singular: string;
    plural: string;
  }> = {
    week: {
      singular: 'Week',
      plural: 'Weeks'
    },
    month: {
      singular: 'Month',
      plural: 'Months'
    },
    year: {
      singular: 'Year',
      plural: 'Years'
    }
  };
  const cycleName = cycleNames[cycle] || {
    singular: cycle,
    plural: cycle
  };
  if (num === 1) {
    return `Every ${cycleName.singular}`;
  }
  return `Every ${num} ${cycleName.plural}`;
};
const OTP_LENGTH = 6;
// Reusable form field component - defined outside to prevent re-creation on render
const FormField = ({
  label,
  required,
  error,
  children
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) => <div data-id="element-2238">
    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2239">
      {label}
      {required && <span className="text-red-500 ml-0.5" data-id="element-2240">*</span>}
    </label>
    {children}
    {error && <p className="text-sm text-red-600 mt-1" data-id="element-2241">{error}</p>}
  </div>;
const inputClass = (hasError?: boolean) => `w-full px-4 py-2.5 border rounded-lg text-gray-900 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${hasError ? 'border-red-300' : 'border-gray-300'}`;
export function PublicSubscriptionView() {
  const {
    hash
  } = useParams<{
    hash: string;
  }>();
  const [subscription, setSubscription] = useState<PublicSubscriptionTemplateData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  // Step 2 state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isConfirmingOtp, setIsConfirmingOtp] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Step 3 state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [usState, setUsState] = useState('');
  const [zip, setZip] = useState('');
  const [step3Errors, setStep3Errors] = useState<Record<string, string>>({});
  // Step 4 state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const captchaWidgetId = useRef<number | null>(null);
  // Step 5 state
  const [subscriptionResult, setSubscriptionResult] = useState<SubscriptionResult | null>(null);
  // Set page title when subscription data loads
  useEffect(() => {
    document.title = 'Billing System';
  }, []);
  useEffect(() => {
    if (subscription) {
      document.title = `${subscription.company.name} ${subscription.name}`;
    }
  }, [subscription]);
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!hash) {
        setError('Invalid subscription link');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await callBillingAPI('publicGetSubscriptionTemplate', {
          hash
        });
        if (!response || Object.keys(response).length === 0) {
          throw new Error('Subscription template not found');
        }
        setSubscription(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load subscription details');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSubscription();
  }, [hash]);
  // Load reCAPTCHA when step 4 is active
  useEffect(() => {
    if (step !== 4) return;
    const renderCaptcha = () => {
      if (captchaContainerRef.current && window.grecaptcha && window.grecaptcha.render) {
        try {
          // Reset if already rendered
          if (captchaWidgetId.current !== null) {
            window.grecaptcha.reset(captchaWidgetId.current);
            setCaptchaToken(null);
            return;
          }
          captchaWidgetId.current = window.grecaptcha.render(captchaContainerRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: (token: string) => {
              setCaptchaToken(token);
            },
            'expired-callback': () => {
              setCaptchaToken(null);
            }
          });
          setCaptchaLoaded(true);
        } catch (e) {
          // Widget may already be rendered
          console.error('reCAPTCHA render error:', e);
        }
      }
    };
    if (window.grecaptcha && window.grecaptcha.render) {
      // Small delay to ensure DOM is ready
      setTimeout(renderCaptcha, 100);
    } else {
      // Load the script
      window.onRecaptchaLoad = renderCaptcha;
      if (!document.querySelector('script[src*="recaptcha"]')) {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }
  }, [step]);
  // Auto-confirm OTP when all digits entered
  const confirmOtp = useCallback(async (code: string[]) => {
    const fullCode = code.join('');
    if (fullCode.length !== OTP_LENGTH || !subscription) return;
    setIsConfirmingOtp(true);
    setOtpError(null);
    try {
      const response = await callBillingAPI('confirmEmailOTP', {
        email,
        code: fullCode
      });
      if (response.confirmed) {
        setEmailConfirmed(true);
      } else {
        setOtpError('Invalid verification code. Please try again.');
        setOtpCode(Array(OTP_LENGTH).fill(''));
        setTimeout(() => {
          otpInputRefs.current[0]?.focus();
        }, 50);
      }
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
      setOtpCode(Array(OTP_LENGTH).fill(''));
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 50);
    } finally {
      setIsConfirmingOtp(false);
    }
  }, [email, subscription]);
  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...otpCode];
    newCode[index] = digit;
    setOtpCode(newCode);
    setOtpError(null);
    if (digit && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
    if (digit && newCode.every(d => d !== '')) {
      confirmOtp(newCode);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otpCode[index] === '' && index > 0) {
        otpInputRefs.current[index - 1]?.focus();
        const newCode = [...otpCode];
        newCode[index - 1] = '';
        setOtpCode(newCode);
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) otpInputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) otpInputRefs.current[index + 1]?.focus();
  };
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newCode = [...otpCode];
    for (let i = 0; i < pasted.length; i++) newCode[i] = pasted[i];
    setOtpCode(newCode);
    const nextEmpty = newCode.findIndex(d => d === '');
    if (nextEmpty !== -1) {
      otpInputRefs.current[nextEmpty]?.focus();
    } else {
      otpInputRefs.current[OTP_LENGTH - 1]?.focus();
      confirmOtp(newCode);
    }
  };
  const handleVerifyEmail = async () => {
    if (!subscription) return;
    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      setEmailError('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setIsVerifyingEmail(true);
    setEmailError(null);
    try {
      const checkResponse = await callBillingAPI('publicCheckEmailExists', {
        company_id: subscription.company_id,
        email: emailTrimmed
      });
      if (checkResponse.exists) {
        setEmailError('This email address is already in use. Please use a different email.');
        setIsVerifyingEmail(false);
        return;
      }
      await callBillingAPI('sendEmailOTP', {
        company_id: subscription.company_id,
        email: emailTrimmed
      });
      setOtpSent(true);
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to verify email. Please try again.');
    } finally {
      setIsVerifyingEmail(false);
    }
  };
  const validateStep3 = (): boolean => {
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!lastName.trim()) errors.lastName = 'Last name is required';
    setStep3Errors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubscribe = async () => {
    if (!subscription || !hash || !captchaToken) return;
    setIsSubscribing(true);
    setSubscribeError(null);
    try {
      const result = await callBillingAPI('publicRunSubscription', {
        hash,
        customer: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          address_street: addressLine.trim(),
          address_city: city.trim(),
          address_state: usState,
          address_zip: zip.trim()
        },
        captcha: {
          token: captchaToken
        }
      });
      setSubscriptionResult(result);
      setStep(5);
    } catch (err) {
      setSubscribeError(err instanceof Error ? err.message : 'Failed to create subscription. Please try again.');
      // Reset captcha
      if (captchaWidgetId.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetId.current);
        setCaptchaToken(null);
      }
    } finally {
      setIsSubscribing(false);
    }
  };
  const handleProceedToStep2 = () => setStep(2);
  const handleProceedToStep3 = () => setStep(3);
  const handleProceedToStep4 = () => {
    if (!validateStep3()) return;
    setStep(4);
  };
  const handleBackToStep1 = () => setStep(1);
  const handleBackToStep2 = () => setStep(2);
  const handleBackToStep3 = () => setStep(3);
  // Get state label from value
  const getStateLabel = (val: string) => US_STATES.find(s => s.value === val)?.label || val;
  // Build address string for summary
  const buildAddressString = () => {
    const parts = [addressLine, city, usState ? getStateLabel(usState) : '', zip].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };
  // Loading state
  if (isLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-2242">
        <Card className="max-w-md w-full text-center py-16" data-id="element-2243">
          <div className="inline-block w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" data-id="element-2244"></div>
          <p className="text-gray-600" data-id="element-2245">Loading subscription details...</p>
        </Card>
      </div>;
  }
  // Error state
  if (error || !subscription) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-2246">
        <Card className="max-w-md w-full text-center py-12 px-6" data-id="element-2247">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4" data-id="element-2248">
            <AlertCircleIcon className="w-8 h-8 text-red-600" data-id="element-2249" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2250">
            Subscription Not Found
          </h2>
          <p className="text-gray-600" data-id="element-2251">
            {error || "The subscription link you're looking for doesn't exist or has expired."}
          </p>
        </Card>
      </div>;
  }
  const subtotal = subscription.items?.reduce((sum, item) => sum + item.qty * item.price, 0) || 0;
  const taxAmount = parseFloat(String(subscription.tax)) || 0;
  const feeAmount = parseFloat(String(subscription.fee)) || 0;
  const totalAmount = parseFloat(String(subscription.amount)) || 0;
  const lineItemColumns = [{
    key: 'description',
    header: 'Description',
    render: (item: ApiSubscriptionItem) => <span className="font-medium text-gray-900" data-id="element-2252">{item.title}</span>
  }, {
    key: 'quantity',
    header: 'Qty',
    render: (item: ApiSubscriptionItem) => <span className="text-gray-600" data-id="element-2253">{item.qty}</span>
  }, {
    key: 'price',
    header: 'Price',
    render: (item: ApiSubscriptionItem) => <span className="text-gray-600" data-id="element-2254">{formatCurrency(item.price)}</span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (item: ApiSubscriptionItem) => <span className="font-medium text-gray-900" data-id="element-2255">
          {formatCurrency(item.qty * item.price)}
        </span>
  }];
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4" data-id="element-2256">
      <div className="max-w-2xl mx-auto space-y-6" data-id="element-2257">
        {/* Company Header Card */}
        <Card className="overflow-hidden border-t-4 border-t-blue-600" data-id="element-2258">
          <div className="p-6 text-center" data-id="element-2259">
            {subscription.company.logo ? <img src={`data:image/png;base64,${subscription.company.logo}`} alt={subscription.company.name} className="w-20 h-20 mx-auto rounded-lg object-contain mb-4" data-id="element-2260" /> : <div className="w-20 h-20 mx-auto bg-blue-100 rounded-lg flex items-center justify-center mb-4" data-id="element-2261">
                <BuildingIcon className="w-10 h-10 text-blue-600" data-id="element-2262" />
              </div>}
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2263">
              {subscription.company.name}
            </h1>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-600" data-id="element-2264">
              {subscription.company.email && <div className="flex items-center gap-1.5" data-id="element-2265">
                  <MailIcon className="w-4 h-4 text-gray-400" data-id="element-2266" />
                  <span data-id="element-2267">{subscription.company.email}</span>
                </div>}
              {subscription.company.phone && <div className="flex items-center gap-1.5" data-id="element-2268">
                  <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-2269" />
                  <span data-id="element-2270">{subscription.company.phone}</span>
                </div>}
            </div>
          </div>
        </Card>

        {/* Step Indicator - hide on step 5 */}
        {step <= 4 && <div className="flex items-center justify-center gap-3" data-id="element-2271">
            {[{
          num: 1,
          label: 'Details'
        }, {
          num: 2,
          label: 'Verify'
        }, {
          num: 3,
          label: 'Info'
        }, {
          num: 4,
          label: 'Review'
        }].map((s, i) => <Fragment key={s.num} data-id="element-2272">
                {i > 0 && <div className={`w-6 sm:w-10 h-0.5 ${step >= s.num ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2273"></div>}
                <div className="flex items-center gap-1.5" data-id="element-2274">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-2275">
                    {s.num}
                  </div>
                  <span className={`text-xs font-medium hidden sm:inline ${step === s.num ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2276">
                    {s.label}
                  </span>
                </div>
              </Fragment>)}
          </div>}

        {/* Step 1: Subscription Details */}
        {step === 1 && <Card data-id="element-2277">
            <div className="p-6 border-b border-gray-200" data-id="element-2278">
              <div className="flex items-start justify-between" data-id="element-2279">
                <div data-id="element-2280">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2281">
                    {subscription.name}
                  </h2>
                  <div className="flex items-center gap-2 mt-2 text-gray-600" data-id="element-2282">
                    <CalendarIcon className="w-4 h-4" data-id="element-2283" />
                    <span data-id="element-2284">
                      {formatBillingCycle(subscription.cycle, subscription.cycle_num)}
                    </span>
                    {subscription.installments > 0 ? <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full ml-2" data-id="element-2285">
                        {subscription.installments}{' '}
                        {Number(subscription.installments) === 1 ? 'Installment' : 'Installments'}
                      </span> : <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full ml-2" data-id="element-2286">
                        Ongoing
                      </span>}
                  </div>
                </div>
                <div className="text-right" data-id="element-2287">
                  <p className="text-2xl font-bold text-gray-900" data-id="element-2288">
                    {formatCurrency(totalAmount)}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-2289">
                    per{' '}
                    {subscription.cycle === 'month' && subscription.cycle_num === 1 ? 'month' : 'cycle'}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6" data-id="element-2290">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-2291">
                Plan Includes
              </h3>
              <div className="border rounded-lg overflow-hidden mb-6" data-id="element-2292">
                <Table columns={lineItemColumns} data={subscription.items || []} keyExtractor={(_, index) => String(index)} emptyMessage="No items" data-id="element-2293" />
              </div>
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-2294">
                <div className="flex justify-between text-sm" data-id="element-2295">
                  <span className="text-gray-600" data-id="element-2296">Subtotal</span>
                  <span className="font-medium text-gray-900" data-id="element-2297">
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                {taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2298">
                    <span className="text-gray-600" data-id="element-2299">
                      {subscription.tax_label || 'Tax'}
                    </span>
                    <span className="font-medium text-gray-900" data-id="element-2300">
                      {formatCurrency(taxAmount)}
                    </span>
                  </div>}
                {feeAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2301">
                    <span className="text-gray-600" data-id="element-2302">
                      {subscription.fee_label || 'Fee'}
                    </span>
                    <span className="font-medium text-gray-900" data-id="element-2303">
                      {formatCurrency(feeAmount)}
                    </span>
                  </div>}
                <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200" data-id="element-2304">
                  <span className="text-gray-900" data-id="element-2305">Total</span>
                  <span className="text-blue-600" data-id="element-2306">
                    {formatCurrency(totalAmount)}
                  </span>
                </div>
              </div>
              <div className="mt-8" data-id="element-2307">
                <Button onClick={handleProceedToStep2} className="w-full py-3 text-base" rightIcon={<ArrowRightIcon className="w-5 h-5" data-id="element-2309" />} data-id="element-2308">
                  Proceed
                </Button>
                <p className="text-center text-xs text-gray-500 mt-3" data-id="element-2310">
                  You'll enter your details in the next step
                </p>
              </div>
            </div>
          </Card>}

        {/* Step 2: Email Verification */}
        {step === 2 && <Card data-id="element-2311">
            <div className="p-6" data-id="element-2312">
              <button type="button" onClick={handleBackToStep1} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6" data-id="element-2313">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2314" />
                Back to details
              </button>
              <div className="text-center mb-8" data-id="element-2315">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 ${emailConfirmed ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-2316">
                  {emailConfirmed ? <CheckCircleIcon className="w-7 h-7 text-green-600" data-id="element-2317" /> : <MailIcon className="w-7 h-7 text-blue-600" data-id="element-2318" />}
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-2319">
                  {emailConfirmed ? 'Email Verified' : 'Verify Your Email'}
                </h2>
                <p className="text-sm text-gray-600" data-id="element-2320">
                  {emailConfirmed ? 'Your email has been verified. Click Proceed to continue.' : !otpSent ? 'Enter your email address to get started' : `We've sent a verification code to ${email}`}
                </p>
              </div>
              <div className="max-w-sm mx-auto space-y-4" data-id="element-2321">
                <div data-id="element-2322">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2323">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                setEmailError(null);
              }} disabled={otpSent} className={`w-full px-4 py-2.5 border rounded-lg text-gray-900 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 ${emailError ? 'border-red-300' : 'border-gray-300'}`} placeholder="you@example.com" onKeyDown={e => {
                if (e.key === 'Enter' && !otpSent) handleVerifyEmail();
              }} data-id="element-2324" />
                  {emailError && <div className="flex items-start gap-1.5 mt-2" data-id="element-2325">
                      <AlertCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2326" />
                      <p className="text-sm text-red-600" data-id="element-2327">{emailError}</p>
                    </div>}
                </div>
                {!otpSent && <Button onClick={handleVerifyEmail} className="w-full" disabled={isVerifyingEmail} isLoading={isVerifyingEmail} leftIcon={!isVerifyingEmail ? <ShieldCheckIcon className="w-4 h-4" data-id="element-2329" /> : undefined} data-id="element-2328">
                    {isVerifyingEmail ? 'Verifying...' : 'Verify Email Address'}
                  </Button>}
                {otpSent && !emailConfirmed && <div className="pt-4" data-id="element-2330">
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-center" data-id="element-2331">
                      Enter verification code
                    </label>
                    <div className="flex items-center justify-center gap-2" data-id="element-2332">
                      {Array.from({
                  length: OTP_LENGTH
                }).map((_, index) => <input key={index} ref={el => {
                  otpInputRefs.current[index] = el;
                }} type="text" inputMode="numeric" maxLength={1} value={otpCode[index]} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} onPaste={index === 0 ? handleOtpPaste : undefined} disabled={isConfirmingOtp} className={`w-11 text-center text-xl font-bold border-2 rounded-lg transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 ${otpError ? 'border-red-300' : otpCode[index] ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} style={{
                  height: '52px'
                }} data-id="element-2333" />)}
                    </div>
                    {isConfirmingOtp && <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-500" data-id="element-2334">
                        <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2335" />
                        Verifying code...
                      </div>}
                    {otpError && <div className="flex items-center justify-center gap-1.5 mt-3" data-id="element-2336">
                        <AlertCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" data-id="element-2337" />
                        <p className="text-sm text-red-600" data-id="element-2338">{otpError}</p>
                      </div>}
                    <button type="button" onClick={() => {
                setOtpSent(false);
                setOtpCode(Array(OTP_LENGTH).fill(''));
                setOtpError(null);
                setEmailConfirmed(false);
              }} className="block mx-auto mt-4 text-sm text-blue-600 hover:text-blue-700 transition-colors" data-id="element-2339">
                      Change email address
                    </button>
                  </div>}
                {emailConfirmed && <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg" data-id="element-2340">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-2341" />
                    <div data-id="element-2342">
                      <p className="text-sm font-medium text-green-900" data-id="element-2343">
                        Email verified
                      </p>
                      <p className="text-xs text-green-700" data-id="element-2344">{email}</p>
                    </div>
                  </div>}
                <div className="pt-4" data-id="element-2345">
                  <Button onClick={handleProceedToStep3} className="w-full" disabled={!emailConfirmed} rightIcon={<ArrowRightIcon className="w-5 h-5" data-id="element-2347" />} data-id="element-2346">
                    Proceed
                  </Button>
                </div>
              </div>
            </div>
          </Card>}

        {/* Step 3: Personal Info & Billing Address */}
        {step === 3 && <Card data-id="element-2348">
            <div className="p-6" data-id="element-2349">
              <button type="button" onClick={handleBackToStep2} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6" data-id="element-2350">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2351" />
                Back to verification
              </button>
              <div className="text-center mb-8" data-id="element-2352">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4" data-id="element-2353">
                  <UserIcon className="w-7 h-7 text-blue-600" data-id="element-2354" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-2355">
                  Your Information
                </h2>
                <p className="text-sm text-gray-600" data-id="element-2356">
                  Enter your personal details and billing address
                </p>
              </div>
              <div className="space-y-6" data-id="element-2357">
                <div data-id="element-2358">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2359">
                    <UserIcon className="w-4 h-4" data-id="element-2360" />
                    Personal Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2361">
                    <FormField label="First Name" required error={step3Errors.firstName} data-id="element-2362">
                      <input type="text" value={firstName} onChange={e => {
                    setFirstName(e.target.value);
                    if (step3Errors.firstName) setStep3Errors(p => {
                      const n = {
                        ...p
                      };
                      delete n.firstName;
                      return n;
                    });
                  }} className={inputClass(!!step3Errors.firstName)} placeholder="John" data-id="element-2363" />
                    </FormField>
                    <FormField label="Last Name" required error={step3Errors.lastName} data-id="element-2364">
                      <input type="text" value={lastName} onChange={e => {
                    setLastName(e.target.value);
                    if (step3Errors.lastName) setStep3Errors(p => {
                      const n = {
                        ...p
                      };
                      delete n.lastName;
                      return n;
                    });
                  }} className={inputClass(!!step3Errors.lastName)} placeholder="Doe" data-id="element-2365" />
                    </FormField>
                  </div>
                  <div className="mt-4" data-id="element-2366">
                    <FormField label="Phone" data-id="element-2367">
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass()} placeholder="(555) 123-4567" data-id="element-2368" />
                    </FormField>
                  </div>
                </div>
                <div data-id="element-2369">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2370">
                    <MapPinIcon className="w-4 h-4" data-id="element-2371" />
                    Billing Address
                  </h3>
                  <div className="space-y-4" data-id="element-2372">
                    <FormField label="Address Line" data-id="element-2373">
                      <input type="text" value={addressLine} onChange={e => setAddressLine(e.target.value)} className={inputClass()} placeholder="123 Main St, Apt 4" data-id="element-2374" />
                    </FormField>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2375">
                      <FormField label="City" data-id="element-2376">
                        <input type="text" value={city} onChange={e => setCity(e.target.value)} className={inputClass()} placeholder="New York" data-id="element-2377" />
                      </FormField>
                      <FormField label="State" data-id="element-2378">
                        <select value={usState} onChange={e => setUsState(e.target.value)} className={inputClass()} data-id="element-2379">
                          {US_STATES.map(s => <option key={s.value} value={s.value} data-id="element-2380">
                              {s.label}
                            </option>)}
                        </select>
                      </FormField>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2381">
                      <FormField label="ZIP Code" data-id="element-2382">
                        <input type="text" value={zip} onChange={e => setZip(e.target.value)} className={inputClass()} placeholder="10001" maxLength={10} data-id="element-2383" />
                      </FormField>
                    </div>
                  </div>
                </div>
                <div className="pt-4" data-id="element-2384">
                  <Button onClick={handleProceedToStep4} className="w-full" rightIcon={<ArrowRightIcon className="w-5 h-5" data-id="element-2386" />} data-id="element-2385">
                    Proceed
                  </Button>
                </div>
              </div>
            </div>
          </Card>}

        {/* Step 4: Review & Subscribe */}
        {step === 4 && <Card data-id="element-2387">
            <div className="p-6" data-id="element-2388">
              <button type="button" onClick={handleBackToStep3} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6" data-id="element-2389">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2390" />
                Back to your information
              </button>

              <div className="text-center mb-8" data-id="element-2391">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4" data-id="element-2392">
                  <ClipboardListIcon className="w-7 h-7 text-blue-600" data-id="element-2393" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-2394">
                  Review & Subscribe
                </h2>
                <p className="text-sm text-gray-600" data-id="element-2395">
                  Please review your details before subscribing
                </p>
              </div>

              <div className="space-y-5" data-id="element-2396">
                {/* Subscription Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2397">
                  <div className="flex items-center justify-between" data-id="element-2398">
                    <div data-id="element-2399">
                      <p className="font-semibold text-gray-900" data-id="element-2400">
                        {subscription.name}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2401">
                        {formatBillingCycle(subscription.cycle, subscription.cycle_num)}
                      </p>
                    </div>
                    <p className="text-xl font-bold text-blue-600" data-id="element-2402">
                      {formatCurrency(totalAmount)}
                    </p>
                  </div>
                </div>

                {/* Personal Info Summary */}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200" data-id="element-2403">
                  <div className="p-4" data-id="element-2404">
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2405">
                      Contact Information
                    </h4>
                    <div className="space-y-1.5" data-id="element-2406">
                      <div className="flex items-center gap-2 text-sm" data-id="element-2407">
                        <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-2408" />
                        <span className="text-gray-900" data-id="element-2409">
                          {firstName} {lastName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm" data-id="element-2410">
                        <MailIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-2411" />
                        <span className="text-gray-900" data-id="element-2412">{email}</span>
                        <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" data-id="element-2413" />
                      </div>
                      {phone && <div className="flex items-center gap-2 text-sm" data-id="element-2414">
                          <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-2415" />
                          <span className="text-gray-900" data-id="element-2416">{phone}</span>
                        </div>}
                    </div>
                  </div>
                  {buildAddressString() && <div className="p-4" data-id="element-2417">
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2418">
                        Billing Address
                      </h4>
                      <div className="flex items-start gap-2 text-sm" data-id="element-2419">
                        <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-2420" />
                        <span className="text-gray-900" data-id="element-2421">
                          {buildAddressString()}
                        </span>
                      </div>
                    </div>}
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-center" data-id="element-2422">
                  <div ref={captchaContainerRef} data-id="element-2423"></div>
                </div>

                {/* Error */}
                {subscribeError && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2424">
                    <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2425" />
                    <p className="text-sm text-red-600" data-id="element-2426">{subscribeError}</p>
                  </div>}

                {/* Subscribe Button */}
                <Button onClick={handleSubscribe} className="w-full py-3 text-base" disabled={!captchaToken || isSubscribing} isLoading={isSubscribing} data-id="element-2427">
                  {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </div>
            </div>
          </Card>}

        {/* Step 5: Thank You */}
        {step === 5 && subscriptionResult && <Card data-id="element-2428">
            <div className="p-8 text-center" data-id="element-2429">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-5" data-id="element-2430">
                <CheckCircleIcon className="w-9 h-9 text-green-600" data-id="element-2431" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2432">
                Subscription Created!
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-2433">
                You have successfully subscribed to{' '}
                <span className="font-semibold" data-id="element-2434">{subscription.name}</span>.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6 text-left space-y-3" data-id="element-2435">
                <div className="flex items-center justify-between" data-id="element-2436">
                  <span className="text-sm text-gray-600" data-id="element-2437">Invoice Number</span>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-2438">
                    #{subscriptionResult.invoice_number}
                  </span>
                </div>
                {subscriptionResult.amount > 0 && <div className="flex items-center justify-between" data-id="element-2439">
                    <span className="text-sm text-gray-600" data-id="element-2440">Amount Due</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-2441">
                      {formatCurrency(subscriptionResult.amount)}
                    </span>
                  </div>}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left" data-id="element-2442">
                <div className="flex items-start gap-3" data-id="element-2443">
                  <MailIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2444" />
                  <p className="text-sm text-blue-800" data-id="element-2445">
                    A confirmation email with your invoice details has been sent
                    to <span className="font-semibold" data-id="element-2446">{email}</span>.
                  </p>
                </div>
              </div>

              {subscriptionResult.amount > 0 && subscriptionResult.invoice_link && <a href={subscriptionResult.invoice_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2447">
                    <CreditCardIcon className="w-5 h-5" data-id="element-2448" />
                    Pay Invoice
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-2449" />
                  </a>}

              {subscriptionResult.amount === 0 && <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-2450">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-2451" />
                  <p className="text-sm text-green-800 text-left" data-id="element-2452">
                    No payment is required at this time. You're all set!
                  </p>
                </div>}
            </div>
          </Card>}
      </div>
    </div>;
}